import React, { useState, useEffect } from 'react';
import { teleMaintenance_Templates, previewTemplate } from '../utils/teleMaintenance';
import SMTPConfigGuide from './SMTPConfigGuide';

const TeleMaintenanceConfig = ({ value, remoteMaintenance, onChange, onRemoteMaintenanceChange }) => {
  const [recipientEmail, setRecipientEmail] = useState(value?.recipientEmail || '');
  const [ccEmails, setCcEmails] = useState(value?.ccEmails?.join(', ') || '');
  const [defaultAccount, setDefaultAccount] = useState(value?.defaultAccount || '');
  const [templateType, setTemplateType] = useState(value?.templateType || 'generic');
  const [customTemplate, setCustomTemplate] = useState(value?.customTemplate || '');
  const [showPreview, setShowPreview] = useState(false);
  const [smtpTestResult, setSMTPTestResult] = useState(null);
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  // NOUVEAU : États pour la gestion des comptes multiples
  const [accounts, setAccounts] = useState(value?.accounts || []);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({
    name: '',
    account: '',
    type: 'generic',
    description: '',
    isDefault: false
  });

  // Mettre à jour le parent quand les valeurs changent
  useEffect(() => {
    const teleMaintenanceConfig = {
      enabled: remoteMaintenance && recipientEmail.trim() !== '',
      recipientEmail: recipientEmail,
      ccEmails: ccEmails ? ccEmails.split(',').map(email => email.trim()).filter(email => email) : [],
      defaultAccount: defaultAccount,
      templateType: templateType,
      customTemplate: customTemplate,
      defaultTemplate: getSelectedTemplate(),
      accounts: accounts // NOUVEAU : Inclure les comptes configurés
    };

    onChange(teleMaintenanceConfig);
  }, [remoteMaintenance, recipientEmail, ccEmails, defaultAccount, templateType, customTemplate, accounts, onChange]);

  const getSelectedTemplate = () => {
    return templateType === 'custom' ? customTemplate : teleMaintenance_Templates[templateType]?.content || '';
  };

  // NOUVEAU : Gestion des comptes
  const addOrUpdateAccount = () => {
    if (!newAccount.name.trim() || !newAccount.account.trim()) {
      alert('Veuillez remplir au moins le nom et le compte.');
      return;
    }

    const accountData = {
      ...newAccount,
      id: editingAccount?.id || Date.now().toString()
    };

    if (editingAccount) {
      // Modification
      setAccounts(accounts.map(acc => acc.id === editingAccount.id ? accountData : acc));
    } else {
      // Ajout
      // Si c'est le premier compte ou si isDefault est coché, le marquer comme défaut
      if (accounts.length === 0 || accountData.isDefault) {
        accountData.isDefault = true;
        // Retirer le statut par défaut des autres comptes
        setAccounts([...accounts.map(acc => ({ ...acc, isDefault: false })), accountData]);
      } else {
        setAccounts([...accounts, accountData]);
      }
    }

    // Reset du formulaire
    setNewAccount({
      name: '',
      account: '',
      type: 'generic',
      description: '',
      isDefault: false
    });
    setShowAccountForm(false);
    setEditingAccount(null);
  };

  const editAccount = (account) => {
    setNewAccount({ ...account });
    setEditingAccount(account);
    setShowAccountForm(true);
  };

  const deleteAccount = (accountId) => {
    if (window.confirm('Voulez-vous vraiment supprimer ce compte ?')) {
      const newAccounts = accounts.filter(acc => acc.id !== accountId);
      setAccounts(newAccounts);
      
      // Si le compte supprimé était par défaut, marquer le premier restant comme défaut
      if (newAccounts.length > 0 && !newAccounts.some(acc => acc.isDefault)) {
        newAccounts[0].isDefault = true;
        setAccounts([...newAccounts]);
      }
    }
  };

  const setAsDefault = (accountId) => {
    setAccounts(accounts.map(acc => ({
      ...acc,
      isDefault: acc.id === accountId
    })));
  };

  const cancelAccountForm = () => {
    setNewAccount({
      name: '',
      account: '',
      type: 'generic',
      description: '',
      isDefault: false
    });
    setShowAccountForm(false);
    setEditingAccount(null);
  };

  const handleTemplateTypeChange = (type) => {
    setTemplateType(type);
    if (type !== 'custom') {
      setCustomTemplate('');
    }
  };

  const testSMTPConfiguration = async () => {
    setTestingSmtp(true);
    setSMTPTestResult(null);

    try {
      const response = await fetch('/api/telemaintenance/test', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      setSMTPTestResult(result);
    } catch (error) {
      setSMTPTestResult({
        success: false,
        message: 'Erreur lors du test de connexion: ' + error.message
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg border">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">📧 Configuration Télémaintenance Automatique</h3>
        <div className={`p-4 rounded-lg border ${
          remoteMaintenance 
            ? 'bg-green-50 border-green-200' 
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center mb-2">
            <svg className={`w-5 h-5 mr-2 ${
              remoteMaintenance ? 'text-green-600' : 'text-yellow-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d={remoteMaintenance 
                  ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                  : "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                } 
              />
            </svg>
            <span className={`font-medium ${
              remoteMaintenance ? 'text-green-800' : 'text-yellow-800'
            }`}>
              {remoteMaintenance 
                ? 'Télémaintenance activée' 
                : 'Télémaintenance non activée'
              }
            </span>
          </div>
          <p className={`text-sm ${
            remoteMaintenance ? 'text-green-700' : 'text-yellow-700'
          }`}>
            {remoteMaintenance 
              ? 'La télémaintenance est requise pour ce client. Configurez les paramètres ci-dessous pour activer l\'envoi automatique d\'emails.' 
              : 'Pour utiliser l\'envoi automatique d\'emails de télémaintenance, activez d\'abord "Télémaintenance à débloquer pour accès aux serveurs" dans les informations générales.'
            }
          </p>
          {!remoteMaintenance && (
            <button
              type="button"
              onClick={() => onRemoteMaintenanceChange(true)}
              className="mt-2 px-3 py-1 text-sm bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
            >
              Activer maintenant
            </button>
          )}
        </div>
      </div>

      {remoteMaintenance && (
        <div className="space-y-6">
          {/* Guide de configuration SMTP */}
          <SMTPConfigGuide />
          
          {/* Test de configuration SMTP */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-800">🧪 Test de configuration SMTP</h4>
              <button
                type="button"
                onClick={testSMTPConfiguration}
                disabled={testingSmtp}
                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded transition-colors flex items-center"
              >
                {testingSmtp ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Test...
                  </>
                ) : 'Tester SMTP'}
              </button>
            </div>
            
            {smtpTestResult && (
              <div className={`p-3 rounded border ${
                smtpTestResult.success 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="flex items-center">
                  {smtpTestResult.success ? (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <span className="text-sm font-medium">{smtpTestResult.message}</span>
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-600 mt-2">
              Ce test vérifie uniquement la connexion au serveur SMTP. Aucun email ne sera envoyé.
            </p>
          </div>
          
          {/* Configuration email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email destinataire * <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="support@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Email principal pour les demandes de télémaintenance</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emails en copie (CC)
              </label>
              <input
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="admin@example.com, tech@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Emails séparés par des virgules</p>
            </div>
          </div>

          {/* NOUVEAU : Gestion des comptes de télémaintenance */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                👤 Comptes de télémaintenance
              </label>
              <button
                type="button"
                onClick={() => setShowAccountForm(true)}
                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Ajouter un compte
              </button>
            </div>
            
            {/* Liste des comptes configurés */}
            {accounts.length > 0 ? (
              <div className="space-y-2 mb-4">
                {accounts.map((account) => (
                  <div key={account.id} className={`p-3 rounded-lg border ${
                    account.isDefault ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800">{account.name}</span>
                          {account.isDefault && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              Par défaut
                            </span>
                          )}
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            account.type === 'generic' ? 'bg-gray-100 text-gray-700' :
                            account.type === 'nominative' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {account.type === 'generic' ? 'Générique' :
                             account.type === 'nominative' ? 'Nominatif' : 'Externe'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Compte: <span className="font-mono bg-gray-100 px-1 rounded">{account.account}</span>
                          {account.description && (
                            <span className="ml-2">• {account.description}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!account.isDefault && (
                          <button
                            type="button"
                            onClick={() => setAsDefault(account.id)}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Définir par défaut
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => editAccount(account)}
                          className="p-1 text-gray-500 hover:text-blue-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteAccount(account.id)}
                          className="p-1 text-gray-500 hover:text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500 mb-4">
                <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <p className="text-sm">Aucun compte configuré</p>
                <p className="text-xs text-gray-400 mt-1">Ajoutez des comptes pour personnaliser vos demandes de télémaintenance</p>
              </div>
            )}

            {/* Formulaire d'ajout/modification de compte */}
            {showAccountForm && (
              <div className="bg-white border-2 border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-800">
                    {editingAccount ? 'Modifier le compte' : 'Ajouter un compte'}
                  </h4>
                  <button
                    type="button"
                    onClick={cancelAccountForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du compte *
                    </label>
                    <input
                      type="text"
                      value={newAccount.name}
                      onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                      placeholder="ex: Compte générique, Administrateur, etc."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Identifiant du compte *
                    </label>
                    <input
                      type="text"
                      value={newAccount.account}
                      onChange={(e) => setNewAccount({...newAccount, account: e.target.value})}
                      placeholder="ex: exolis, adm.exolis, ext-hoppen-spada"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de compte
                    </label>
                    <select
                      value={newAccount.type}
                      onChange={(e) => setNewAccount({...newAccount, type: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="generic">Générique</option>
                      <option value="nominative">Nominatif</option>
                      <option value="external">Externe/Consultant</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optionnel)
                    </label>
                    <input
                      type="text"
                      value={newAccount.description}
                      onChange={(e) => setNewAccount({...newAccount, description: e.target.value})}
                      placeholder="ex: Pour interventions critiques, montées de version..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={newAccount.isDefault}
                      onChange={(e) => setNewAccount({...newAccount, isDefault: e.target.checked})}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isDefault" className="ml-2 text-sm text-gray-700">
                      Définir comme compte par défaut
                    </label>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={cancelAccountForm}
                      className="px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={addOrUpdateAccount}
                      className="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                    >
                      {editingAccount ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              Les comptes configurés ici pourront être sélectionnés lors de l'envoi des demandes de télémaintenance.
              Le compte par défaut sera présélectionné automatiquement.
            </p>
          </div>

          {/* Sélection du template */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template de demande
            </label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              {Object.entries(teleMaintenance_Templates).map(([key, template]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTemplateTypeChange(key)}
                  className={`p-3 text-sm rounded-md border transition-colors ${
                    templateType === key
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {template.name}
                </button>
              ))}
            </div>

            {/* Template personnalisé */}
            {templateType === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template personnalisé
                </label>
                <textarea
                  value={customTemplate}
                  onChange={(e) => setCustomTemplate(e.target.value)}
                  placeholder="Votre template personnalisé...\n\nVariables disponibles:\n{CLIENT_NAME} - Nom du client\n{ACCOUNT_NAME} - Nom du compte\n{REASON} - Raison de la demande\n{VERSION} - Version\n{DATE} - Date du jour"
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>
            )}
          </div>

          {/* Prévisualisation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Aperçu du template
              </label>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {showPreview ? 'Masquer' : 'Afficher'} l'aperçu
              </button>
            </div>
            
            {showPreview && (
              <div className="bg-gray-50 border rounded-md p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Aperçu avec variables d'exemple:</div>
                <div className="bg-white p-3 rounded border text-sm whitespace-pre-line font-mono">
                  {previewTemplate(getSelectedTemplate(), {
                    clientName: 'Client Exemple',
                    accountName: defaultAccount || 'compte-exemple',
                    reason: 'un problème de connexion patient',
                    version: '7.41.13'
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Variables disponibles */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">💡 Variables disponibles dans les templates:</h4>
            <div className="text-xs text-blue-700 grid grid-cols-1 md:grid-cols-2 gap-1">
              <div><code className="bg-blue-100 px-1 rounded">{'{CLIENT_NAME}'}</code> - Nom du client</div>
              <div><code className="bg-blue-100 px-1 rounded">{'{ACCOUNT_NAME}'}</code> - Nom du compte</div>
              <div><code className="bg-blue-100 px-1 rounded">{'{REASON}'}</code> - Raison de la demande</div>
              <div><code className="bg-blue-100 px-1 rounded">{'{VERSION}'}</code> - Version (pour montées de version)</div>
              <div><code className="bg-blue-100 px-1 rounded">{'{DATE}'}</code> - Date du jour</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeleMaintenanceConfig;